import { Link, Text } from "@react-pdf/renderer";
import { ComponentProps, ReactNode } from "react";
import { disableWordHyphenation } from "./physicalComponentPdfUtils";

type PdfStyle = ComponentProps<typeof Link>["style"];

const linkStyle: PdfStyle = {
	textDecoration: "underline",
};

function renderPdfHtmlNodes(
	nodes: NodeListOf<ChildNode>,
	keyPrefix = "",
): ReactNode[] {
	const result: ReactNode[] = [];

	nodes.forEach((node, index) => {
		const key = `${keyPrefix}-${index}`;

		if (node.nodeType === Node.TEXT_NODE) {
			if (node.textContent) {
				result.push(node.textContent);
			}
			return;
		}

		if (node.nodeType !== Node.ELEMENT_NODE) {
			return;
		}

		const element = node as Element;
		const tag = element.tagName.toLowerCase();
		const children = renderPdfHtmlNodes(element.childNodes, key);

		switch (tag) {
			case "strong":
			case "b":
				result.push(
					<Text
						key={key}
						style={{ fontWeight: 700 }}
						hyphenationCallback={disableWordHyphenation}
					>
						{children}
					</Text>,
				);
				break;
			case "em":
			case "i":
				result.push(
					<Text
						key={key}
						style={{ fontStyle: "italic" }}
						hyphenationCallback={disableWordHyphenation}
					>
						{children}
					</Text>,
				);
				break;
			case "a": {
				const href = element.getAttribute("href");
				if (href) {
					result.push(
						<Link key={key} src={href} style={linkStyle}>
							{children}
						</Link>,
					);
				} else {
					result.push(...children);
				}
				break;
			}
			case "br":
				result.push("\n");
				break;
			default:
				result.push(...children);
		}
	});

	return result;
}

interface PdfRichTextProps {
	children: string;
	style?: PdfStyle;
}

export function PdfRichText({ children, style }: PdfRichTextProps) {
	if (!children.trim()) {
		return null;
	}

	const doc = new DOMParser().parseFromString(children, "text/html");
	const nodes = renderPdfHtmlNodes(doc.body.childNodes);

	return (
		<Text style={style} hyphenationCallback={disableWordHyphenation}>
			{nodes}
		</Text>
	);
}
